import { authFetch } from "../auth-context";
import type { Quotation } from "../lib/reviewReferences";

export interface RevisionPack {
  message_id: number;
  submitted_at: string;
  attachments: Array<{ filename: string; url: string; bytes: number | null }>;
}
export async function getRevisionPacks(companyId: string, threadId: number): Promise<RevisionPack[]> {
  const response = await authFetch(`/api/quotations/revision/${encodeURIComponent(companyId)}/${threadId}`);
  if (!response.ok) {
    // Keep the server's code: an older plugin (e.g. 1.100.x) has no pack endpoint and answers
    // 409 revision_details_unsupported, which ApprovalReview shows as a calm fallback line.
    const code = await response.json().then((b: { error?: unknown }) => String(b?.error ?? ""), () => "");
    throw new Error(code === "revision_details_unsupported"
      ? "revision_details_unsupported"
      : "Submission details unavailable. Refresh to retry.");
  }
  const body = await response.json();
  if (!Array.isArray(body.revisionPacks)) throw new Error("Submission details unavailable.");
  return body.revisionPacks;
}

export async function getQuotations(): Promise<{ quotations: Quotation[]; bridgeCompanyIds: string[] }> {
  const response = await authFetch("/api/quotations");
  if (!response.ok) throw new Error("Could not load quotations. Try again.");
  const body = await response.json();
  if (!Array.isArray(body.quotations)) throw new Error("Quotation response is incomplete. Refresh to check.");
  return { quotations: body.quotations, bridgeCompanyIds: Array.isArray(body.bridgeCompanyIds) ? body.bridgeCompanyIds : [] };
}
/** A refused quotation request. `message` is plain English; `code` is the server's exact
 *  reason code (kept for support and tooltips, never discarded). */
export class QuotationRequestError extends Error {
  code: string | null; status: number;
  constructor(message: string, code: string | null, status: number) { super(message); this.name = "QuotationRequestError"; this.code = code; this.status = status; }
}
const QUOTATION_ERROR_TEXT: Record<string, string> = {
  quotation_changed: "This quotation changed. Refresh and review the latest version before approving.",
  quotation_not_approvable: "This quotation isn't a draft any more, so it can't be sent. Refresh to see where it stands.",
  quotation_not_feasible: "The agent marked this as something we can't do, so it can't be sent to the client.",
  quotation_not_cancellable: "This quotation can't be withdrawn in its current state. Refresh to see where it stands.",
  quotation_bridge_unavailable: "This site's private quotation connection isn't set up, so nothing was sent.",
  quotation_registration_changed: "The website connection changed. Ask the agent to re-check this quotation before sending.",
  quotation_request_conflict: "A different decision was already recorded for this quotation. Refresh and check it before trying again.",
  quotation_receipt_unconfirmed: "The website didn't confirm it received this. Refresh to check its status before retrying.",
  quotation_transport_unconfirmed: "The website couldn't be reached to confirm this. Refresh to check its status before retrying.",
  quotation_publish_rejected: "The website refused this quotation. Nothing new was shown to the client.",
  quotation_thread_unavailable: "The client's request is closed or missing on the website, so this quotation can't go to it.",
  quotation_active_thread_required: "The client's request is closed or missing on the website, so this quotation can't go to it.",
  quotation_not_found: "This quotation no longer exists. Refresh to see the current list.",
  quotation_retry_unavailable: "There's no saved decision left to retry. Refresh to see where it stands.",
  review_company_mismatch: "This quotation belongs to a different client record. Refresh and try again.",
  invalid_cancellation_note: "The withdrawal message wasn't accepted (too long or invalid). Nothing was sent.",
  company_not_found: "This client is no longer registered on the dashboard.",
  rspd_quote_review_authorization: "The website didn't accept our approval signature, so nothing was sent to the client.",
  rspd_quote_review_signature: "The website didn't accept our approval signature, so nothing was sent to the client.",
  rspd_quote_review_configuration: "Owner review isn't switched on at the website, so it can't publish. Nothing was sent to the client.",
};
/** Plain words for a quotation reason code; unknown codes are named rather than hidden. */
export function quotationErrorText(code: string | null | undefined): string | null {
  if (!code) return null;
  if (Object.hasOwn(QUOTATION_ERROR_TEXT, code)) return QUOTATION_ERROR_TEXT[code];
  if (code.startsWith("quotation_money_invalid")) return "The price or amounts didn't pass the money check, so nothing was sent. Ask the agent to correct the figures.";
  return /^[a-z0-9_.:-]{1,80}$/i.test(code) ? `The website refused it (reason code: ${code}).` : null;
}
async function refusalCode(response: Response): Promise<string | null> {
  let body: { error?: unknown } | null = null;
  try { body = await response.json(); } catch { /* No readable body: fall back to the status. */ }
  return typeof body?.error === "string" && body.error ? body.error.slice(0, 200) : null;
}

export async function decideQuotation(quote: Quotation, action: "approve" | "cancel", requestId: string, note?: string): Promise<void> {
  const response = await authFetch(`/api/quotations/${encodeURIComponent(quote.id)}/decision`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ companyId: quote.companyId, version: quote.version, contentHash: quote.contentHash, action, requestId, ...(note ? { note } : {}) }),
  });
  if (!response.ok) {
    const code = await refusalCode(response);
    const known = quotationErrorText(code);
    if (known) throw new QuotationRequestError(known, code, response.status);
    if (response.status === 409) throw new QuotationRequestError("This quotation changed. Refresh and review the latest version before approving.", code, response.status);
    throw new QuotationRequestError(action === "approve" ? "Publication was not confirmed. Refresh to check its status before retrying." : "Cancellation was not confirmed. Refresh to check its status before retrying.", code, response.status);
  }
}

/** Client-visible note for a withdrawal made to send a new price. */
export const REQUOTE_NOTE = "We're withdrawing this quotation because a revised quote is on its way. Please don't act on the previous price; no work or charge has been approved under it.";

/** Retries the existing saved decision, never creates a second approval. */
export async function retryQuotation(quote: Quotation): Promise<void> {
  const response = await authFetch(`/api/quotations/${encodeURIComponent(quote.id)}/retry`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ companyId: quote.companyId, version: quote.version, contentHash: quote.contentHash }),
  });
  if (!response.ok) {
    const code = await refusalCode(response);
    throw new QuotationRequestError(quotationErrorText(code) ?? "The saved decision could not be completed. Refresh to check its status; the approved terms are unchanged.", code, response.status);
  }
}
